import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  createContato,
  deleteContato,
  getContato,
  updateContato,
} from '../services/contatoService';
import { getSessionClientId, getSessionRole } from '../services/authSession';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCpf, formatPhone } from '../utils/formatters';
import useSubmitLock from '../hooks/useSubmitLock';

const INITIAL_FORM_DATA = {
  nome: '',
  cpf: '',
  telefone: '',
  enderecoEmail: '',
};

/*const mockEmpresas = [
  { id_empresa: '1', razao_social: 'Kretos Lab' },
  { id_empresa: '2', razao_social: 'Concreteira Azul' },
];

const mockContatos = [
  {
    id: 'cont-1',
    id_cliente: '1',
    id_empresa: '1',
    tipo: 'tecnico',
    nome: 'Carlos Silva',
    telefone: '11987654321',
    enderecoEmail: 'carlos.silva@empresa.com',
    criado_em: '2026-04-01T10:00:00.000Z',
  },
];*/

// Normaliza um contato da API para o formato da tela (resiliente a snake/camel).
function normalizeContato(item) {
  // Mantem a pagina resiliente quando o backend alterna nomes de campos.
  return {
    id: String(item.id || item.id_contato || item.idContato || `cont-${Date.now()}-${Math.random()}`),
    idCliente: String(item.id_cliente || item.idCliente || ''),
    nome: item.nome || item.nome_contato || item.nomeCompleto || '-',
    cpf: onlyDigits(item.cpf || item.cpf_contato || item.cpfContato || item.numrCpf || ''),
    telefone: onlyDigits(item.telefone || item.numr_telefone || item.numrTelefone || ''),
    enderecoEmail: item.enderecoEmail || item.endereco_email || item.email || '',
  };
}

// Tela de Gestão de Contatos: CRUD de contatos (nome, CPF, telefone, email); exclusão restrita a admin.
function ContatosPage() {
  const role = getSessionRole();
  const sessionClientId = getSessionClientId();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContatoId, setEditingContatoId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [contatos, setContatos] = useState([]);
  const [viewContato, setViewContato] = useState(null);

  // Carrega os contatos da API ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadContatos = async () => {
      // Busca contatos
      const [contatosResult] = await Promise.allSettled([
        getContato(),
      ]);

      if (!isMounted) return;

      if (contatosResult.status === 'fulfilled' && Array.isArray(contatosResult.value)) {
        setContatos(contatosResult.value.map(normalizeContato));
      }

      setLoading(false);
    };

    loadContatos().catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredContatos = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return contatos;

    return contatos.filter((contato) => {
      const nome = contato.nome || '';
      const cpf = formatCpf(contato.cpf) || '';
      const telefone = formatPhone(contato.telefone);
      const email = contato.enderecoEmail || '';

      return (
        nome.toLowerCase().includes(normalizedQuery) ||
        cpf.toLowerCase().includes(normalizedQuery) ||
        telefone.toLowerCase().includes(normalizedQuery) ||
        email.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query, contatos]);

  const stats = useMemo(() => {
    const total = contatos.length;
    //const tecnicos = contatos.filter((item) => normalizeTipo(item.tipo) === 'tecnico').length;
    //const financeiros = contatos.filter((item) => normalizeTipo(item.tipo) === 'financeiro').length;
    //const administrativos = contatos.filter((item) => normalizeTipo(item.tipo) === 'administrativo').length;
    return { total };
  }, [contatos]);

  // Handler de onChange por campo do formulário.
  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Abre o modal em modo de criação (reseta o formulário).
  const openModal = () => {
    setFormErrors({});
    setEditingContatoId(null);
    setFormData(INITIAL_FORM_DATA);
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com máscaras aplicadas.
  const openEditModal = (contato) => {
    setFormErrors({});
    setEditingContatoId(contato.id);
    setFormData({
      nome: contato.nome || '',
      cpf: formatCpf(contato.cpf || ''),
      telefone: formatPhone(contato.telefone || ''),
      enderecoEmail: contato.enderecoEmail || '',
    });
    setIsModalOpen(true);
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingContatoId(null);
    setFormErrors({});
  };

  // Valida obrigatórios (nome/telefone/email) e formato; retorna erros + telefone sem máscara.
  const validateForm = () => {
    const errors = {};

    if (!String(formData.nome || '').trim()) {
      errors.nome = 'Nome obrigatório.';
    }

    const phoneDigits = onlyDigits(formData.telefone);
    if (!phoneDigits) {
      errors.telefone = 'Telefone obrigatório.';
    } else if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      errors.telefone = 'Telefone inválido.';
    }

    if (!String(formData.enderecoEmail || '').trim()) {
      errors.enderecoEmail = 'Email obrigatório.';
    } else if (!/\S+@\S+\.\S+/.test(String(formData.enderecoEmail).trim())) {
      errors.enderecoEmail = 'Email inválido.';
    }

    return { errors, phoneDigits };
  };

  // Submete o form: valida, checa duplicado (nome+CPF+telefone), cria/atualiza na API e refaz a lista.
  const [submitting, handleSave] = useSubmitLock(async (event) => {
    event.preventDefault();

    const { errors, phoneDigits } = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const duplicate = contatos.some((item) => {
      if (editingContatoId && item.id === editingContatoId) return false;
      return (
        item.nome.trim().toLowerCase() === formData.nome.trim().toLowerCase() &&
        onlyDigits(item.cpf) === onlyDigits(formData.cpf) &&
        onlyDigits(item.telefone) === phoneDigits
      );
    });

    if (duplicate) {
      setFormErrors({ nome: 'Contato já cadastrado com este telefone.' });
      return;
    }

    const effectiveClientId = formData.idCliente || sessionClientId;
    if (!effectiveClientId) {
      setFormErrors({ geral: 'Cliente da sessão não encontrado.' });
      return;
    }

    try {
      if (editingContatoId) {
        const { response, payload } = await updateContato(editingContatoId, formData, effectiveClientId);
        const nextContato = response ? normalizeContato(response) : normalizeContato({ id: editingContatoId, ...payload });

        setContatos((prev) => prev.map((item) => (item.id === editingContatoId ? nextContato : item)));
        showSuccessToast('Contato atualizado com sucesso.');
      } else {
        const { response, payload } = await createContato(formData, effectiveClientId);
        const nextContato = response
          ? normalizeContato(response)
          : normalizeContato({ id: `cont-${Date.now()}`, ...payload, idCliente: sessionClientId || '' });

        setContatos((prev) => [nextContato, ...prev]);
        showSuccessToast('Contato cadastrado com sucesso.');
      }

      closeModal();
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar o contato.' });
    }
  });

  // Exclui o contato após confirmação (restrito a admin) e remove da lista local.
  const handleDelete = async (idContato) => {
    if (!canManage) return;

    const confirm = window.confirm('Deseja realmente excluir este contato?');
    if (!confirm) return;

    try {
      await deleteContato(idContato);
      setContatos((prev) => prev.filter((item) => item.id !== idContato));
      showSuccessToast('Contato excluído com sucesso.');
    } catch (error) {
      showErrorToast(error?.message || 'Não foi possível excluir o contato.');
    }
  };

  return (
    <div className="contatos-page space-y-6">
      <section className="contatos-header flex flex-col gap-4 rounded-2xl bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de Contatos</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie os contatos cadastrados no sistema
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          disabled={!canCreate}
        >
          <Plus className="h-4 w-4" />
          Novo contato
        </button>
      </section>

      <section className="contatos-metrics grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Tecnicos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.tecnicos}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Financeiros</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.financeiros}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Administrativos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.administrativos}</p>
        </div>
      </section>

      <section className="contatos-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por nome, telefone, email ou empresa"
            resultCount={filteredContatos.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">CPF</th>
                <th className="px-4 py-3">Telefone</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">
                  <div className={`flex items-center ${canManage ? 'justify-between gap-3' : 'justify-end'}`}>
                    <span>Ações</span>
                    {canManage ? (
                      <ExpandableSearchFilter
                        variant="inline"
                        query={query}
                        onChange={setQuery}
                        placeholder="Buscar..."
                        resultCount={filteredContatos.length}
                      />
                    ) : null}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`sk-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 5 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredContatos.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                    Nenhum contato encontrado.
                  </td>
                </tr>
              ) : (
                filteredContatos.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4 font-medium text-slate-900">{item.nome || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatCpf(item.cpf) || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{formatPhone(item.telefone) || '-'}</td>
                    <td className="px-4 py-4 text-slate-600">{item.enderecoEmail || '-'}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewContato(item)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditModal(item)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.id)}
                              className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-sk-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredContatos.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum contato encontrado.
            </div>
          ) : (
            filteredContatos.map((item) => (
              <div key={item.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-slate-900">{item.nome || '-'}</p>
                <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600">
                  <span>CPF: {formatCpf(item.cpf) || '-'}</span>
                  <span>Telefone: {formatPhone(item.telefone) || '-'}</span>
                  <span>Email: {item.enderecoEmail || '-'}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewContato(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  {canManage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditModal(item)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 transition hover:border-red-300 hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Excluir
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewContato}
        onClose={() => setViewContato(null)}
        title="Detalhes do contato"
        sections={viewContato ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Nome', value: viewContato.nome || '-' },
              { label: 'CPF', value: formatCpf(viewContato.cpf) || '-' },
              { label: 'Telefone', value: formatPhone(viewContato.telefone) || '-' },
              { label: 'Email', value: viewContato.enderecoEmail || '-' },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <form
            onSubmit={handleSave}
            className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-4 shadow-xl sm:p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Cadastro</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingContatoId ? 'Editar contato' : 'Cadastrar contato'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Preencha os dados do contato para uso nas telas de cadastro
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {formErrors.geral ? (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formErrors.geral}
              </div>
            ) : null}

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="contato-nome" className="text-sm font-medium text-slate-700">
                  Nome <span className="text-rose-500">*</span>
                </label>
                <input
                  id="contato-nome"
                  type="text"
                  value={formData.nome}
                  onChange={handleFieldChange('nome')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.nome ? <p className="text-xs text-rose-600">{formErrors.nome}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="contato-cpf" className="text-sm font-medium text-slate-700">
                  CPF:
                </label>
                <input
                  id="contato-cpf"
                  type="text"
                  value={formData.cpf}
                  onChange={(event) => setFormData((prev) => ({ ...prev, cpf: formatCpf(event.target.value) }))}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.cpf ? <p className="text-xs text-rose-600">{formErrors.cpf}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="contato-telefone" className="text-sm font-medium text-slate-700">
                  Telefone <span className="text-rose-500">*</span>
                </label>
                <input
                  id="contato-telefone"
                  type="text"
                  value={formData.telefone}
                  onChange={(event) => setFormData((prev) => ({ ...prev, telefone: formatPhone(event.target.value) }))}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.telefone ? <p className="text-xs text-rose-600">{formErrors.telefone}</p> : null}
              </div>

              <div className="space-y-2">
                <label htmlFor="contato-email" className="text-sm font-medium text-slate-700">
                  Email <span className="text-rose-500">*</span>
                </label>
                <input
                  id="contato-email"
                  type="email"
                  value={formData.enderecoEmail}
                  onChange={handleFieldChange('enderecoEmail')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.enderecoEmail ? <p className="text-xs text-rose-600">{formErrors.enderecoEmail}</p> : null}
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="w-full rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 sm:w-auto"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? 'Salvando...' : editingContatoId ? 'Salvar alterações' : 'Salvar contato'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ContatosPage;

